import { defineStore, acceptHMRUpdate } from 'pinia';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import {
    fetchTeams,
    createTeam,
    deleteTeam,
    updateTeamDetails,
    fetchTeamMembers,
    updateTeamMembers,
} from '@/stores/api/teams.api';

import { useSubscriptionStore } from '@/stores/subscription.store';

interface TeamMember {
    id: string;
    [key: string]: unknown;
}

interface Team {
    id: string;
    groupName: string;
    members?: TeamMember[];
    membersFetched?: boolean;
    [key: string]: unknown;
}

interface TeamsPatch {
    op: 'add' | 'remove';
    value: string[];
}

interface TeamsState {
    error: unknown;
    fetching: boolean;
    map: Record<string, Team>;
}

export function getInitialState (): TeamsState {
    return {
        error: null,
        fetching: false,
        map: {},
    };
}

export const useTeamsStore = defineStore('teams', {
    state: getInitialState,
    getters: {
        list: (state): Team[] => values(state.map),
        teamById: (state) => (id: string): Team | undefined => state.map[id],
        teamByName (): (groupName: string) => Team | undefined {
            return (groupName) => (this.list as Team[]).find(
                (team) => team.groupName.toLowerCase().trim() === groupName.toLowerCase().trim(),
            );
        },
        showTeams (): boolean {
            return (useSubscriptionStore().getFeatureFlag('enableTeams') || useSubscriptionStore().isScimEnabled);
        },
        teamIdsByUserIdMap (): Record<string, string[]> {
            const teamIdsByUserId: Record<string, string[]> = {};
            (this.list as Team[]).forEach((team) => {
                team.members?.forEach((member) => {
                    const teamIds = teamIdsByUserId[member.id] ?? [];
                    teamIds.push(team.id);
                    teamIdsByUserId[member.id] = teamIds;
                });
            });

            return teamIdsByUserId;
        },
        teamsByUserId (): (userId: string) => Team[] {
            return (userId) => {
                const teamIds = (this.teamIdsByUserIdMap as Record<string, string[]>)[userId] ?? [];

                return teamIds.map((teamId) => (this.map as Record<string, Team>)[teamId]);
            };
        },
        isLoadingTeams: (state): boolean => state.fetching,
    },
    actions: {
        setMap (map: Record<string, Team>) {
            this.map = map;
        },
        setMapAtKey ({ id, team }: { id: string; team: Team }) {
            this.map[id] = team;
        },
        deleteMapKey ({ id }: { id: string }) {
            delete this.map[id];
        },
        setError (error: unknown) {
            this.error = error;
        },
        setFetching (fetching: boolean) {
            this.fetching = fetching;
        },
        applyTeamsPatch ({ user, patch }: { user: TeamMember; patch: TeamsPatch[] }) {
            patch.forEach(({ op, value: teamIds }) => {
                switch (op) {
                    case 'add':
                        teamIds.forEach((teamId) => {
                            const members = this.map[teamId].members ?? [];
                            if (members.some((member) => member.id === user.id)) return;
                            members.push(user);
                            this.map[teamId].members = members;
                        });
                        break;
                    case 'remove':
                        teamIds.forEach((teamId) => {
                            this.map[teamId].members = this.map[teamId]?.members?.filter(
                                (member) => member.id !== user.id,
                            );
                        });
                        break;
                }
            });
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isEmpty(this.map) && !reFetch) {
                return;
            }

            this.setFetching(true);

            try {
                const teamList = await fetchTeams();
                this.setMap(keyBy(teamList, 'id') as Record<string, Team>);
            } catch (error) {
                this.setError(error);
            } finally {
                this.setFetching(false);
            }
        },
        async createTeam (team: Partial<Team>) {
            this.setFetching(true);

            try {
                const newTeam = await createTeam(team);
                this.setMapAtKey({
                    id: newTeam.id,
                    team: newTeam,
                });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching(false);
            }
        },
        async deleteTeam ({ id }: { id: string }) {
            this.setFetching(true);

            try {
                await deleteTeam(id);
                this.deleteMapKey({ id });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching(false);
            }
        },
        async updateTeamDetails (team: Team) {
            this.setFetching(true);

            try {
                const updatedTeam = await updateTeamDetails(team);

                const oldTeam = this.map[team.id];
                const mergedTeam = {
                    ...updatedTeam,
                    members: oldTeam?.members || [],
                };

                this.setMapAtKey({
                    id: updatedTeam.id,
                    team: mergedTeam,
                });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching(false);
            }
        },
        async loadTeamMembers ({ id, reFetch = false }: { id: string; reFetch?: boolean }) {
            if (this.map[id]?.membersFetched && !reFetch) {
                return;
            }

            this.setFetching(true);

            try {
                const teamWithMembers = await fetchTeamMembers(id);
                const mergedTeam = {
                    ...teamWithMembers,
                    membersFetched: true,
                };
                this.setMapAtKey({
                    id,
                    team: mergedTeam,
                });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching(false);
            }
        },
        async updateTeamMembers ({ id, members }: { id: string; members: TeamMember[] }) {
            this.setFetching(true);

            try {
                const updatedTeam = await updateTeamMembers(members, id);
                const mergedTeam = {
                    ...updatedTeam,
                    membersFetched: true,
                };
                this.setMapAtKey({
                    id,
                    team: mergedTeam,
                });
            } catch (error) {
                this.setError(error);
                throw error;
            } finally {
                this.setFetching(false);
            }
        },
        teardown () {
            this.$reset();
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useTeamsStore, module.hot));
}
